// GPX / KML / GeoJSON import & export helpers

function escapeXml(s) {
    if (s === undefined || s === null) return "";
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}

// ----- EXPORT -----

export function toGPX(track) {
    const { name, points, pois } = track;
    const time = new Date().toISOString();
    const wpts = (pois || [])
        .map(
            (p) =>
                `  <wpt lat="${p.lat}" lon="${p.lng}">${
                    typeof p.ele === "number"
                        ? `\n    <ele>${p.ele}</ele>`
                        : ""
                }\n    <name>${escapeXml(p.name || "POI")}</name>${
                    p.desc
                        ? `\n    <desc>${escapeXml(p.desc)}</desc>`
                        : ""
                }\n  </wpt>`,
        )
        .join("\n");
    const trkpts = (points || [])
        .map(
            (p) =>
                `      <trkpt lat="${p.lat}" lon="${p.lng}">${
                    typeof p.ele === "number"
                        ? `<ele>${p.ele}</ele>`
                        : ""
                }</trkpt>`,
        )
        .join("\n");
    return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="RutaForge" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${escapeXml(name || "Ruta sin título")}</name>
    <time>${time}</time>
  </metadata>
${wpts}
  <trk>
    <name>${escapeXml(name || "Ruta sin título")}</name>
    <trkseg>
${trkpts}
    </trkseg>
  </trk>
</gpx>`;
}

export function toKML(track) {
    const { name, points, pois } = track;
    const coords = (points || [])
        .map(
            (p) =>
                `${p.lng},${p.lat}${
                    typeof p.ele === "number" ? `,${p.ele}` : ",0"
                }`,
        )
        .join(" ");
    const placemarks = (pois || [])
        .map(
            (p) => `    <Placemark>
      <name>${escapeXml(p.name || "POI")}</name>
      <description>${escapeXml(p.desc || "")}</description>
      <Point><coordinates>${p.lng},${p.lat}${
                typeof p.ele === "number" ? `,${p.ele}` : ",0"
            }</coordinates></Point>
    </Placemark>`,
        )
        .join("\n");
    return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(name || "Ruta sin título")}</name>
${placemarks}
    <Placemark>
      <name>${escapeXml(name || "Ruta sin título")}</name>
      <LineString>
        <tessellate>1</tessellate>
        <coordinates>${coords}</coordinates>
      </LineString>
    </Placemark>
  </Document>
</kml>`;
}

export function toGeoJSON(track) {
    const { name, points, pois } = track;
    return JSON.stringify(
        {
            type: "FeatureCollection",
            features: [
                ...(pois || []).map((p) => ({
                    type: "Feature",
                    properties: { name: p.name, desc: p.desc, kind: "poi" },
                    geometry: {
                        type: "Point",
                        coordinates: [
                            p.lng,
                            p.lat,
                            ...(typeof p.ele === "number" ? [p.ele] : []),
                        ],
                    },
                })),
                {
                    type: "Feature",
                    properties: { name: name || "Ruta sin título", kind: "track" },
                    geometry: {
                        type: "LineString",
                        coordinates: (points || []).map((p) => [
                            p.lng,
                            p.lat,
                            ...(typeof p.ele === "number" ? [p.ele] : []),
                        ]),
                    },
                },
            ],
        },
        null,
        2,
    );
}

export function download(filename, content, mime) {
    const blob = new Blob([content], { type: mime || "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ----- IMPORT -----

export function parseGPX(text) {
    const doc = new DOMParser().parseFromString(text, "application/xml");
    const err = doc.querySelector("parsererror");
    if (err) throw new Error("GPX inválido");
    const nameEl =
        doc.querySelector("metadata > name") || doc.querySelector("trk > name");
    const name = nameEl ? nameEl.textContent.trim() : "";
    const points = [];
    doc.querySelectorAll("trkpt").forEach((el) => {
        const lat = parseFloat(el.getAttribute("lat"));
        const lng = parseFloat(el.getAttribute("lon"));
        const eleEl = el.querySelector("ele");
        const ele = eleEl ? parseFloat(eleEl.textContent) : undefined;
        points.push({ lat, lng, ele });
    });
    // Also try rtept if no trkpt
    if (points.length === 0) {
        doc.querySelectorAll("rtept").forEach((el) => {
            const lat = parseFloat(el.getAttribute("lat"));
            const lng = parseFloat(el.getAttribute("lon"));
            const eleEl = el.querySelector("ele");
            const ele = eleEl ? parseFloat(eleEl.textContent) : undefined;
            points.push({ lat, lng, ele });
        });
    }
    const pois = [];
    doc.querySelectorAll("wpt").forEach((el) => {
        const lat = parseFloat(el.getAttribute("lat"));
        const lng = parseFloat(el.getAttribute("lon"));
        const nm = el.querySelector("name")?.textContent || "POI";
        const ds = el.querySelector("desc")?.textContent || "";
        const eleEl = el.querySelector("ele");
        const ele = eleEl ? parseFloat(eleEl.textContent) : undefined;
        pois.push({ lat, lng, name: nm, desc: ds, ele });
    });
    return { name, points, pois };
}

export function parseKML(text) {
    const doc = new DOMParser().parseFromString(text, "application/xml");
    const err = doc.querySelector("parsererror");
    if (err) throw new Error("KML inválido");
    const name = doc.querySelector("Document > name")?.textContent || "";
    const points = [];
    const pois = [];
    doc.querySelectorAll("Placemark").forEach((pm) => {
        const line = pm.querySelector("LineString > coordinates");
        const pt = pm.querySelector("Point > coordinates");
        const pmName = pm.querySelector("name")?.textContent || "";
        const pmDesc = pm.querySelector("description")?.textContent || "";
        if (line) {
            line.textContent
                .trim()
                .split(/\s+/)
                .forEach((c) => {
                    const [lng, lat, ele] = c.split(",").map(parseFloat);
                    if (!isNaN(lat) && !isNaN(lng))
                        points.push({
                            lat,
                            lng,
                            ele: !isNaN(ele) ? ele : undefined,
                        });
                });
        } else if (pt) {
            const [lng, lat, ele] = pt.textContent.trim().split(",").map(parseFloat);
            if (!isNaN(lat) && !isNaN(lng))
                pois.push({
                    lat,
                    lng,
                    name: pmName,
                    desc: pmDesc,
                    ele: !isNaN(ele) ? ele : undefined,
                });
        }
    });
    return { name, points, pois };
}

export function parseGeoJSON(text) {
    const data = JSON.parse(text);
    let name = "";
    const points = [];
    const pois = [];
    const features =
        data.type === "FeatureCollection"
            ? data.features
            : data.type === "Feature"
              ? [data]
              : [];
    features.forEach((f) => {
        const geom = f.geometry;
        if (!geom) return;
        if (geom.type === "LineString") {
            if (f.properties?.name) name = f.properties.name;
            geom.coordinates.forEach(([lng, lat, ele]) => {
                points.push({ lat, lng, ele });
            });
        } else if (geom.type === "Point") {
            const [lng, lat, ele] = geom.coordinates;
            pois.push({
                lat,
                lng,
                ele,
                name: f.properties?.name || "POI",
                desc: f.properties?.desc || f.properties?.description || "",
            });
        }
    });
    return { name, points, pois };
}

export function parseAny(filename, text) {
    const ext = filename.toLowerCase().split(".").pop();
    if (ext === "gpx") return parseGPX(text);
    if (ext === "kml") return parseKML(text);
    if (ext === "geojson" || ext === "json") return parseGeoJSON(text);
    // Try sniff
    if (text.trim().startsWith("<")) {
        if (text.includes("<gpx")) return parseGPX(text);
        if (text.includes("<kml")) return parseKML(text);
    }
    return parseGeoJSON(text);
}
